import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import Popover from '../../base/Popover';
import { CREATE_ACCOUNT_ITEM, PRO_ROLES_ENABLED, REGISTER_PRO_ITEMS, type NavItem } from './navItems';

/**
 * «سجل معنا»: the guest's green sign-up button in the top bar (owner, 2026-10;
 * replaces «ابدأ الآن»). Signed-in users never see it.
 *
 * - With professional roles on, it opens a menu: «إنشاء حساب», then the
 *   professional sign-ups (developer, broker and marketer, property and
 *   facilities management, appraiser), each a deep link into /register?role=.
 * - With them off it is a plain link to /register.
 *
 * The menu follows the APG menu-button pattern: Enter, Space or ArrowDown opens
 * it on the first item, ArrowUp on the last; arrows, Home and End move between
 * items; Escape closes it and returns to the button; Tab closes it and moves on.
 * It hangs below the button in a Popover (portalled, never clipped, always
 * opening downward, aligned to the button's inline-end edge so it stays on
 * screen at the end of the bar).
 */

/** Primary-button look (Button variant="primary" size="sm"), shared by the link and menu versions. */
const CTA_CLASS = [
  'inline-flex min-h-[2.75rem] shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl px-4',
  'text-[0.875rem] font-semibold text-white bg-[#004D40] shadow-brand-xs transition-colors duration-200',
  'hover:bg-[#003930] active:bg-[#002A23]',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004D40] focus-visible:ring-offset-2',
  'dark:bg-[#3FBBA2] dark:text-[#002A23] dark:hover:bg-[#7FD6C3] dark:focus-visible:ring-[#3FBBA2] dark:focus-visible:ring-offset-[#041C18]',
].join(' ');

const ITEM_CLASS = [
  'flex min-h-[3rem] w-full items-center gap-3 rounded-lg px-2.5 py-1.5 text-start',
  'text-[var(--pv-color-text)] transition-colors duration-150',
  'hover:bg-[var(--pv-color-surface-sunken)] focus:outline-none focus-visible:bg-[var(--pv-color-surface-sunken)]',
  'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--pv-color-primary)]',
].join(' ');

interface RegisterMenuProps {
  className?: string;
}

export default function RegisterMenu({ className = '' }: RegisterMenuProps) {
  const { t } = useTranslation('nav');
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemsRef = useRef<Array<HTMLAnchorElement | null>>([]);
  const firstItemRef = useRef<HTMLAnchorElement | null>(null);
  const lastItemRef = useRef<HTMLAnchorElement | null>(null);
  // Which item takes focus when the menu opens (ArrowUp opens on the last one).
  const [focusEdge, setFocusEdge] = useState<'first' | 'last'>('first');
  const menuId = useId();
  const buttonId = useId();

  if (!PRO_ROLES_ENABLED) {
    return (
      <Link to={CREATE_ACCOUNT_ITEM.to} className={`${CTA_CLASS} ${className}`}>
        {t('register.cta')}
      </Link>
    );
  }

  const items: NavItem[] = [CREATE_ACCOUNT_ITEM, ...REGISTER_PRO_ITEMS];
  const lastIndex = items.length - 1;

  const openAt = (edge: 'first' | 'last') => {
    setFocusEdge(edge);
    setOpen(true);
  };

  const close = () => setOpen(false);

  const focusItem = (index: number) => {
    const count = items.length;
    itemsRef.current[((index % count) + count) % count]?.focus();
  };

  const onButtonKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openAt('first');
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      openAt('last');
    }
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = itemsRef.current.findIndex((el) => el === document.activeElement);
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusItem(current + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusItem(current < 0 ? lastIndex : current - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusItem(0);
        break;
      case 'End':
        event.preventDefault();
        focusItem(lastIndex);
        break;
      case ' ':
        // Space activates a menuitem (APG); a link only reacts to Enter natively.
        if (current >= 0) {
          event.preventDefault();
          itemsRef.current[current]?.click();
        }
        break;
      case 'Tab':
        // The menu is portalled to <body>, so the browser's next stop after the
        // last item would be the end of the page. Hand focus back to the button
        // and let this same Tab carry on from there.
        close();
        buttonRef.current?.focus();
        break;
      default:
        break;
    }
  };

  const renderItem = (item: NavItem, index: number) => (
    <Link
      key={item.to}
      ref={(el) => {
        itemsRef.current[index] = el;
        if (index === 0) firstItemRef.current = el;
        if (index === lastIndex) lastItemRef.current = el;
      }}
      to={item.to}
      role="menuitem"
      tabIndex={-1}
      onClick={close}
      className={ITEM_CLASS}
    >
      <span
        className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)]"
        aria-hidden="true"
      >
        <i className={`${item.icon} text-[1.125rem] leading-none`} />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="text-[0.9375rem] font-semibold leading-snug">{t(item.labelKey)}</span>
        {item.descKey && (
          <span className="text-[0.875rem] leading-snug text-[var(--pv-color-text-muted)]">{t(item.descKey)}</span>
        )}
      </span>
    </Link>
  );

  return (
    <>
      <button
        ref={buttonRef}
        id={buttonId}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close() : openAt('first'))}
        onKeyDown={onButtonKeyDown}
        className={`${CTA_CLASS} ${className}`}
      >
        {t('register.cta')}
        <ChevronDown
          className={`h-4 w-4 opacity-80 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      <Popover
        open={open}
        onOpenChange={setOpen}
        anchorRef={buttonRef}
        width="19rem"
        align="end"
        id={menuId}
        role="menu"
        aria-labelledby={buttonId}
        initialFocusRef={focusEdge === 'last' ? lastItemRef : firstItemRef}
        className="p-1.5"
      >
        <div onKeyDown={onMenuKeyDown}>
          {renderItem(items[0], 0)}
          <div role="separator" className="my-1.5 h-px bg-[var(--pv-border-soft)]" />
          <div role="group" aria-labelledby={`${menuId}-pro`}>
            {/* Names the group; presentational so the menu owns only menu roles. */}
            <p
              id={`${menuId}-pro`}
              role="presentation"
              className="px-2.5 pb-1 pt-0.5 text-[0.875rem] font-semibold text-[var(--pv-color-text-muted)]"
            >
              {t('register.asProfessional')}
            </p>
            {items.slice(1).map((item, i) => renderItem(item, i + 1))}
          </div>
        </div>
      </Popover>
    </>
  );
}
